import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useReturnRequests, {
  ensureReturnRequestPage,
  UseReturnRequestsQueryOptions,
} from "@babackapp/private-api-client/client/use-return-requests";
import { useReturnRequestsFilters } from "../../providers/use-return-requests-filters";
import { useReturnRequestsSerializedFilter } from "./use-return-requests-serialized-filter";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnSort } from "@tanstack/react-table";

function getSortBy(sorting: ColumnSort[]) {
  // Map column IDs to server field names for sorting
  const sortFieldMapping: Record<string, string> = {
    order: "orderName",
  };

  return sorting?.[0]?.id
    ? (sortFieldMapping[sorting[0].id] || sorting[0].id)
    : undefined;
}


export function useFilteredReturnRequests() {
  const { data: shop } = useCurrentShopSuspense();

  const { sorting, search, quickFilter, page } = useReturnRequestsFilters();
  const filter = useReturnRequestsSerializedFilter();

  const sortBy = getSortBy(sorting);

  const query = useReturnRequests({
    shopId: shop.id,
    search,
    sortBy: sortBy as UseReturnRequestsQueryOptions["sortBy"],
    sortOrder: sorting?.[0]?.desc ? "desc" : "asc",
    filter,
    page,
    quickFilter: quickFilter ?? undefined,
  });

  return query;
}


/**
 * Helper function to navigate between return requests
 * easily
 */
export function useReturnRequestsNavigation(currentReturnRequestId: string) {
  const { data: shop } = useCurrentShopSuspense();
  const { data } = useFilteredReturnRequests();
  const { sorting, search, quickFilter, page } = useReturnRequestsFilters();
  const filter = useReturnRequestsSerializedFilter();
  const queryClient = useQueryClient();

  const currentPage = data?.data ?? [];
  const hasMorePages = data?.hasMore ?? false;

  const ensurePage = (page: number) => {
    const sortBy = getSortBy(sorting);

    return ensureReturnRequestPage(queryClient, {
      shopId: shop.id,
      search,
      sortBy: sortBy as UseReturnRequestsQueryOptions["sortBy"],
      sortOrder: sorting?.[0]?.desc ? "desc" : "asc",
      filter,
      page,
      quickFilter: quickFilter ?? undefined,
    });
  };

  const returnRequestIndexInPage = currentPage?.findIndex(
    (returnRequest) => returnRequest.id === currentReturnRequestId,
  );

  // Do not allow going to a previous page when already at the first element
  // of the first page. Pages are 1-indexed in the API, so requesting page 0
  // would result in a negative $skip value server side.
  const canGoPrevious = !(returnRequestIndexInPage === 0 && page <= 1);

  const canGoNext = !(
    returnRequestIndexInPage === currentPage?.length - 1 && !hasMorePages
  );

  const getPreviousId = async () => {
    if (!canGoPrevious) return null;
    if (returnRequestIndexInPage > 0)
      return currentPage[returnRequestIndexInPage - 1].id;
    const result = await ensurePage(page - 1);
    const data = result.data[result.data.length - 1];
    const previousId = data.id;
    return previousId;
  };

  const getNextId = async () => {
    if (!canGoNext) return null;
    if (returnRequestIndexInPage < currentPage.length - 1)
      return currentPage[returnRequestIndexInPage + 1].id;
    const result = await ensurePage(page + 1);
    const data = result.data[0];
    const nextId = data.id;
    return nextId;
  };

  const disabled = !currentPage.length;

  return {
    disabled,
    canGoNext,
    canGoPrevious,
    getPreviousId,
    getNextId,
  };
}
